import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaGraduationCap } from 'react-icons/fa';
import { Eye, EyeOff, ArrowRight, ArrowLeft, CheckCircle, Upload } from 'lucide-react';
import Nav from '../components/nav';
import SEO from '../components/SEO';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/high-res.css';
import { baseUrl } from '../config';

const STEPS = [
  { number: 1, title: 'Account' },
  { number: 2, title: 'Program Code' },
  { number: 3, title: 'Verify ID' },
  { number: 4, title: 'Secure Account' },
];

const inputClass =
  'w-full px-4 py-3 border border-slate-300 rounded-xl text-slate-800 bg-white ' +
  'focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100 transition-all';

// Duplicated from terms&conditions.jsx's termsAndConditionsSections — that
// file exports a full page (Nav + SEO + tabs), not a content-only component,
// and this data lives in a local const inside it, so nothing here is
// currently importable. Extracting it into a shared component both files
// import would be the cleaner long-term fix; flagging rather than doing it
// silently since it touches a second file. Supplemental terms (Career Page /
// Distributor / Location Scouting) are omitted — not relevant to a student
// signup.
const TERMS_SECTIONS = [
  {
    title: '1. Acceptance of Terms',
    content:
      'By accessing or using the Producer Buddy platform, you agree to these Terms and Conditions and our Privacy Policy. If you do not agree with any part of these terms, please discontinue use of the platform.',
  },
  {
    title: '2. Use of the Platform',
    content:
      'You agree to use Producer Buddy only for lawful purposes and in compliance with all applicable laws and regulations.',
    subtitle: 'Prohibited Uses:',
    subpoints: [
      '• Engaging in illegal activities.',
      '• Attempting unauthorized access to the platform, data, or systems.',
      '• Harassing, threatening, or exploiting others, including through bullying.',
      '• Uploading or sharing content that infringes intellectual property rights or contains malicious code (e.g., viruses).',
      '• Posting inappropriate or adult-oriented content on public or blog pages meant for general audiences.',
    ],
  },
  {
    title: '3. User Accounts',
    content: 'To access certain features, users may be required to create an account. You agree to:',
    subpoints: [
      '• Provide accurate and up-to-date registration information.',
      '• Maintain the confidentiality of your credentials.',
      '• Notify us immediately of unauthorized access or suspicious activity.',
    ],
    additional: 'Producer Buddy reserves the right to suspend or terminate accounts that violate these terms.',
  },
  {
    title: '4. Intellectual Property',
    content:
      "All content on the platform—including text, graphics, logos, software, and images—is the property of Pilomation or its content suppliers and is protected by intellectual property laws. Users may not reproduce, distribute, or create derivative works without explicit written permission.",
  },
  {
    title: '5. Content and Data',
    content:
      'You retain ownership of the content you upload (e.g., scripts, project data). By uploading content, you grant Producer Buddy a non-exclusive, royalty-free, worldwide license to use, display, and modify your content as necessary to provide our services. We take appropriate measures to protect your data in line with our Privacy Policy.',
  },
  {
    title: '6. Payments and Subscriptions',
    subcategories: [
      {
        subtitle: 'Payment Obligations:',
        subpoints: [
          "• Users must pay applicable fees and taxes for paid features or services.",
          "• Payments must be made through the platform's designated payment gateway.",
        ],
      },
      {
        subtitle: 'Subscription Features:',
        subpoints: [
          '• Additional storage, premium access, and expert services (like Solution Engineers) may incur extra charges, displayed clearly at the time of purchase.',
        ],
      },
      {
        subtitle: 'Billing and Refunds:',
        subpoints: [
          '• Timely payment is required to maintain access.',
          '• Refunds and cancellations are subject to the terms shown during the payment process and may not always be available.',
          '• Producer Buddy is not liable for payment network issues, failed transactions, or delays.',
        ],
      },
    ],
  },
  {
    title: '7. Terms of Use for Marketing and KPI Data',
    subpoints: [
      '• Key Performance Indicators (KPIs) collected through the platform may be utilized for marketing and promotional purposes by Producer Buddy.',
      '• Any data used for marketing will be anonymized and aggregated to ensure user confidentiality and privacy.',
      "• By using the platform, users consent to the use of KPIs for marketing initiatives, as outlined in the platform's Privacy Policy.",
    ],
  },
  {
    title: '8. Limitation of Liability',
    content:
      'Producer Buddy provides the platform on an "as-is" and "as-available" basis. We do not guarantee that the platform will be error-free, secure, or continuously available. To the maximum extent permitted by law, Producer Buddy and its affiliates are not liable for any indirect, incidental, or consequential damages, including loss of profits, data, or business opportunities, arising from the use of or inability to use the platform.',
    subtitle: 'Additionally:',
    subpoints: [
      '• The platform is not a substitute for professional advice and should not be relied upon for legal, financial, or medical decisions without consulting licensed and qualified experts.',
      "• While Producer Buddy's AI tool provides detailed budgeting estimates, any budget adjustments or negotiations that occur after the release of the project are outside the scope of the tool and are the sole responsibility of the user.",
    ],
  },
  {
    title: '9. Modifications to Terms',
    content:
      'We may revise these terms from time to time with reasonable notice. Continued use of the platform constitutes your acceptance of the revised terms.',
  },
  {
    title: '10. Governing Law',
    content:
      'These terms shall be governed by and construed in accordance with the laws of India. Any disputes arising out of or in connection with these terms shall be subject to the exclusive jurisdiction of the courts located in your location, India or other countries where the platform is available.',
  },
  {
    title: '11. Contact Information',
    content: 'For questions or concerns, contact us at:',
    subpoints: ['• Email: [support@producersbuddy.com]'],
  },
  {
    title: '12. Entire Agreement',
    content: 'These Terms, together with the Privacy Policy, form the entire agreement between you and Producer Buddy.',
  },
];

const StudentProgramSignup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isContinueEntry = location.pathname === '/student-program/continue';
  const fileInputRef = useRef(null);

  const [isResuming, setIsResuming] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // ── Step 1 state ──────────────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordValidationError, setPasswordValidationError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);

  // ── Step 2 state ──────────────────────────────────────────────────────────
  const [programCode, setProgramCode] = useState('');
  const [rollNumber, setRollNumber] = useState('');
  const [dob, setDob] = useState(null);
  const [codeError, setCodeError] = useState('');
  const [matchedInstitution, setMatchedInstitution] = useState(null);
  const [lastVerifiedCode, setLastVerifiedCode] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState('');

  // ── Step 3 state ──────────────────────────────────────────────────────────
  const [verifyIdFile, setVerifyIdFile] = useState(null);
  const [verifyIdPreview, setVerifyIdPreview] = useState(null);
  const [isVerifyingId, setIsVerifyingId] = useState(false);
  const [verifyIdError, setVerifyIdError] = useState('');
  const [idVerified, setIdVerified] = useState(false);
  const verifyIdInputRef = useRef(null);

  // ── Resume-on-mount: mirrors CompleSingUp.jsx's token-driven guard ────────
  useEffect(() => {
    const token = localStorage.getItem('access_token');

    if (!token) {
      // /student-program/continue only makes sense for an existing session —
      // route_login.py only ever sends someone here right after a successful
      // /login call, which always sets access_token first. No token means a
      // stale/bookmarked link, not a real resume.
      if (isContinueEntry) {
        navigate('/login');
        return;
      }
      setIsResuming(false);
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${baseUrl}/getUserById`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          localStorage.removeItem('access_token');
          if (isContinueEntry) { navigate('/login'); return; }
          setCurrentStep(1);
          return;
        }
        const user = await res.json();
        // A student already marked id_verified (self-service OCR match, or
        // manually approved by an admin after a failed attempt) skips
        // straight past Step 3. Step 3 re-entry after a page reload (for a
        // student mid-wizard but not yet verified) still isn't resumable —
        // that gap is pre-existing, not something this change fixes.
        if (user.user_type === 'student' && user.id_verified) {
          setCurrentStep(4);
        } else {
          setCurrentStep(user.user_type === 'student' ? 2 : 1);
        }
      } catch (_) {
        setCurrentStep(1);
      } finally {
        setIsResuming(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Step 1 validation (mirrors signup.jsx) ────────────────────────────────
  const validatePassword = (pw) =>
    /^(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.*\d)[A-Za-z\d!@#$%^&*]{10,}$/.test(pw);

  const handlePasswordChange = (e) => {
    const val = e.target.value;
    setPassword(val);
    setPasswordValidationError(
      validatePassword(val)
        ? ''
        : 'Password must be at least 10 characters long, include 1 capital letter, 1 special character, and 1 numerical digit.'
    );
    setPasswordError(val !== confirmPassword && confirmPassword ? 'Passwords do not match' : '');
  };

  const handleConfirmPasswordChange = (e) => {
    const val = e.target.value;
    setConfirmPassword(val);
    setPasswordError(val !== password ? 'Passwords do not match' : '');
  };

  const handleUsernameChange = (e) => {
    const val = e.target.value.toLowerCase();
    setUsername(val);
    const regex = /^(?=.*[a-z])(?=.*\d)[a-z\d]{5,}$/;
    setUsernameError(
      regex.test(val) ? '' : 'Username must contain both lowercase letters and numbers and be at least 5 characters long.'
    );
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProfileImageFile(file);
    setProfileImagePreview(URL.createObjectURL(file));
  };

  // ── Step 1 submit (password path) ─────────────────────────────────────────
  const handleStep1Submit = async (e) => {
    e.preventDefault();
    if (usernameError || passwordValidationError || passwordError) return;
    if (password !== confirmPassword) { setPasswordError('Passwords do not match'); return; }

    setIsLoading(true);
    setErrorMessage('');
    try {
      let profilePictureUrl;
      if (profileImageFile) {
        const fd = new FormData();
        fd.append('file', profileImageFile);
        fd.append('file_type', 'image');
        fd.append('user_id', 0);
        const uploadRes = await fetch(`${baseUrl}/upload/direct`, { method: 'POST', body: fd });
        if (!uploadRes.ok) throw new Error('Failed to upload profile image. Please try again.');
        const uploadData = await uploadRes.json();
        profilePictureUrl = uploadData.public_url;
      }

      const signupRes = await fetch(`${baseUrl}/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          password,
          email,
          phone_number: phone,
          country: '',
          year_experience: 0,
          user_type: 'student',
          role: null,
          ...(profilePictureUrl ? { profile_picture: profilePictureUrl } : {}),
        }),
      });
      if (!signupRes.ok) {
        const err = await signupRes.json().catch(() => ({}));
        const detail = err.detail;
        throw new Error(
          typeof detail === 'string'
            ? detail
            : Array.isArray(detail)
            ? detail.map((d) => d.msg || JSON.stringify(d)).join(', ')
            : 'Signup failed. Please try again.'
        );
      }

      const loginRes = await axios.post(
        `${baseUrl}/login`,
        new URLSearchParams({ username, password }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      );
      const { access_token } = loginRes.data;
      localStorage.setItem('access_token', access_token);

      if (name.trim()) {
        const [first_name, ...rest] = name.trim().split(' ');
        const last_name = rest.join(' ') || null;
        await fetch(`${baseUrl}/updateUser`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${access_token}` },
          body: JSON.stringify({ first_name, last_name }),
        }).catch(() => {}); // Non-fatal — name is cosmetic, don't block the wizard on it
      }

      setCurrentStep(2);
    } catch (err) {
      setErrorMessage(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Step 1 Google path ────────────────────────────────────────────────────
  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      const response = await axios.post(`${baseUrl}/auth/google`, {
        token: credentialResponse.credential,
        email: decoded.email,
        name: decoded.name,
        picture: decoded.picture,
        google_id: decoded.sub,
      });
      const { access_token } = response.data;
      localStorage.setItem('access_token', access_token);
      setCurrentStep(2);
    } catch (error) {
      setErrorMessage(error.response?.data?.detail || 'Google signup failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => setErrorMessage('Google signup failed. Please try again.');

  // ── Step 2: program code / roll number / DOB ──────────────────────────────
  const handleProgramCodeChange = (e) => {
    const val = e.target.value.toUpperCase();
    setProgramCode(val);
    // Code changed after already confirming a different code — the previous
    // confirmation no longer applies to what's in the field now.
    if (termsAccepted) {
      setTermsAccepted(false);
      setMatchedInstitution(null);
      setLastVerifiedCode(null);
    }
  };

  const openTermsModal = () => {
    if (!programCode.trim()) { setCodeError('Enter your student program code first.'); return; }
    if (!dob) { setCodeError('Enter your date of birth first.'); return; }
    setCodeError('');
    setConfirmError('');
    setHasScrolledToBottom(false);
    setShowTermsModal(true);
  };

  const handleTermsScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 24) {
      setHasScrolledToBottom(true);
    }
  };

  const handleConfirmTerms = async () => {
    setIsConfirming(true);
    setConfirmError('');
    try {
      const token = localStorage.getItem('access_token');
      const trimmedCode = programCode.trim();

      let institution = matchedInstitution;
      if (!institution || lastVerifiedCode !== trimmedCode) {
        const verifyRes = await fetch(`${baseUrl}/student-program/verify-code`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ code: trimmedCode }),
        });
        if (!verifyRes.ok) {
          throw new Error(verifyRes.status === 404 ? 'Invalid or inactive program code.' : 'Could not verify code. Please try again.');
        }
        institution = await verifyRes.json();
        setMatchedInstitution(institution);
        setLastVerifiedCode(trimmedCode);
      }

      const confirmRes = await fetch(`${baseUrl}/student-program/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code: trimmedCode }),
      });
      if (!confirmRes.ok) {
        throw new Error('Could not confirm enrollment. Please try again.');
      }

      setTermsAccepted(true);
      setShowTermsModal(false);
    } catch (err) {
      setConfirmError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsConfirming(false);
    }
  };

  // ── Step 3: ID card upload / verification ─────────────────────────────────
  const handleVerifyIdFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setVerifyIdFile(file);
    setVerifyIdPreview(URL.createObjectURL(file));
    setVerifyIdError('');
  };

  const removeVerifyIdFile = () => {
    setVerifyIdFile(null);
    setVerifyIdPreview(null);
    setVerifyIdError('');
    if (verifyIdInputRef.current) verifyIdInputRef.current.value = '';
  };

  const handleVerifyId = async () => {
    if (!verifyIdFile) return;
    setIsVerifyingId(true);
    setVerifyIdError('');
    try {
      const token = localStorage.getItem('access_token');
      const fd = new FormData();
      fd.append('image', verifyIdFile);
      fd.append('roll_number', rollNumber);
      // Build YYYY-MM-DD from the Date object's LOCAL getters, not
      // toISOString() (which converts to UTC first and shifts the day back
      // by one for any timezone ahead of UTC, e.g. IST, whenever local
      // midnight falls before the UTC boundary catches up - exactly the
      // reproduced off-by-one-day bug).
      const dobLocal = `${dob.getFullYear()}-${String(dob.getMonth() + 1).padStart(2, '0')}-${String(dob.getDate()).padStart(2, '0')}`;
      fd.append('dob', dobLocal);

      const res = await fetch(`${baseUrl}/student-program/verify-id`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      // A non-200 here is always a backend/infra failure - the endpoint's
      // own mismatch verdict is still HTTP 200 with {verified: false}.
      if (!res.ok) throw new Error('__generic__');

      const data = await res.json();
      if (data.verified) {
        setIdVerified(true);
        setTimeout(() => setCurrentStep(4), 1200);
      } else {
        setVerifyIdError(data.message || "We couldn't verify your ID automatically.");
      }
    } catch (err) {
      setVerifyIdError('Something went wrong, please try again.');
    } finally {
      setIsVerifyingId(false);
    }
  };

  const handleFinishSignup = async () => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${baseUrl}/student-program/complete-signup`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('__generic__');
      navigate('/dashboard');
    } catch (err) {
      setErrorMessage('Something went wrong, please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Plain-text stepper (no circles/numbers/checkmarks) ────────────────────
  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-4 mb-8 flex-wrap">
      {STEPS.map((step, index) => {
        const isCurrent = currentStep === step.number;
        const isDone = currentStep > step.number;
        return (
          <React.Fragment key={step.number}>
            <span
              className={`text-sm transition-all ${
                isCurrent
                  ? 'text-teal-600 font-bold'
                  : isDone
                  ? 'text-teal-400 font-medium'
                  : 'text-slate-400 font-normal'
              }`}
            >
              {step.title}
            </span>
            {index < STEPS.length - 1 && <span className="w-6 h-px bg-slate-200" />}
          </React.Fragment>
        );
      })}
    </div>
  );

  if (isResuming) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Student Program Sign Up"
        description="Join ProducerBuddy's Student Program with your institution's enrollment code."
        url="https://producerbuddy.pilomation.com/student-program/signup"
        robots="noindex, nofollow"
      />
      <Nav />
      <div className="min-h-screen flex items-center justify-center bg-white py-12 px-4">
        <div className="relative w-full max-w-xl bg-white border border-slate-200 rounded-3xl shadow-xl p-8 md:p-10">
          {isLoading && (
            <div className="absolute inset-0 bg-white/90 backdrop-blur-sm flex flex-col items-center justify-center gap-3 rounded-3xl z-10">
              <div className="w-10 h-10 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin" />
              <p className="text-teal-700 font-semibold">Working on it...</p>
            </div>
          )}

          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center text-white bg-gradient-to-br from-teal-600 to-cyan-600 shadow-lg">
              <FaGraduationCap size={30} />
            </div>
            <h1 className="text-2xl font-bold text-slate-800">Student Program</h1>
            <p className="text-slate-500 text-sm mt-1">A few quick steps to unlock your student benefits</p>
          </div>

          {renderStepIndicator()}

          {errorMessage && (
            <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-xl px-4 py-3 mb-4">
              {errorMessage}
            </div>
          )}

          {currentStep === 1 && (
            <form className="animate-in fade-in" onSubmit={handleStep1Submit}>
              <div className="mb-4">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  useOneTap={false}
                  theme="outline"
                  size="large"
                  text="signup_with"
                  shape="rectangular"
                  width="100%"
                />
              </div>

              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs text-slate-400 font-medium">OR SIGN UP WITH EMAIL</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              <div className="flex flex-col items-center mb-6">
                <div
                  className="w-20 h-20 rounded-full border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 cursor-pointer hover:border-teal-600 overflow-hidden transition-all"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {profileImagePreview ? (
                    <img src={profileImagePreview} alt="Profile preview" className="w-full h-full object-cover" />
                  ) : (
                    <Upload size={22} />
                  )}
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={handleProfileImageChange}
                  className="hidden"
                  disabled={isLoading}
                />
                <span className="text-xs text-slate-400 mt-1.5">Profile photo (optional)</span>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-slate-800 mb-1.5">Full Name</label>
                <input type="text" className={inputClass} value={name} onChange={(e) => setName(e.target.value)} disabled={isLoading} required />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-slate-800 mb-1.5">Username</label>
                <input type="text" className={inputClass} value={username} onChange={handleUsernameChange} disabled={isLoading} required />
                {usernameError && <p className="text-red-600 text-xs mt-1">{usernameError}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-slate-800 mb-1.5">Email</label>
                <input type="email" className={inputClass} value={email} onChange={(e) => setEmail(e.target.value)} disabled={isLoading} required />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-slate-800 mb-1.5">Phone Number</label>
                <div className="relative w-full" translate="no">
                  <PhoneInput
                    country={'in'}
                    value={phone}
                    onChange={setPhone}
                    containerClass="phoneFieldFix"
                    inputClass={inputClass}
                    inputProps={{ required: true, disabled: isLoading }}
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-slate-800 mb-1.5">Password</label>
                <div className="relative flex items-center">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className={inputClass}
                    value={password}
                    onChange={handlePasswordChange}
                    disabled={isLoading}
                    required
                  />
                  <button type="button" className="absolute right-3 text-slate-400 hover:text-slate-600" onClick={() => setShowPassword((s) => !s)}>
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {passwordValidationError && <p className="text-red-600 text-xs mt-1">{passwordValidationError}</p>}
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-slate-800 mb-1.5">Confirm Password</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className={inputClass}
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  disabled={isLoading}
                  required
                />
                {passwordError && <p className="text-red-600 text-xs mt-1">{passwordError}</p>}
              </div>

              <button
                type="submit"
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-white bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                disabled={isLoading}
              >
                {isLoading ? 'Creating Account...' : 'Continue'} <ArrowRight size={18} />
              </button>
            </form>
          )}

          {currentStep === 2 && (
            <div>
              <div className="mb-4">
                <label className="block text-sm font-semibold text-slate-800 mb-1.5">Student Program Code</label>
                <input
                  type="text"
                  className={inputClass}
                  value={programCode}
                  onChange={handleProgramCodeChange}
                  placeholder="e.g. WWI-PB-2026"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-slate-800 mb-1.5">Roll Number</label>
                <input
                  type="text"
                  className={inputClass}
                  value={rollNumber}
                  onChange={(e) => setRollNumber(e.target.value)}
                  placeholder="As it appears on your ID card"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-slate-800 mb-1.5">Date of Birth</label>
                <DatePicker
                  selected={dob}
                  onChange={(date) => setDob(date)}
                  maxDate={new Date()}
                  showYearDropdown
                  dropdownMode="select"
                  placeholderText="Select your date of birth"
                  className={inputClass}
                />
              </div>

              {codeError && <p className="text-red-600 text-xs mb-3">{codeError}</p>}

              <div
                className="flex items-start gap-3 mb-4 cursor-pointer"
                onClick={openTermsModal}
              >
                <div
                  className={`w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                    termsAccepted ? 'bg-teal-600 border-teal-600' : 'border-slate-300'
                  }`}
                >
                  {termsAccepted && <CheckCircle size={14} className="text-white" />}
                </div>
                <span className="text-sm text-slate-700">
                  I agree to the <span className="text-teal-600 underline font-medium">Terms &amp; Conditions</span>
                </span>
              </div>

              {termsAccepted && matchedInstitution && (
                <div className="flex items-center gap-3 bg-teal-50 border border-teal-200 text-teal-700 font-semibold rounded-xl px-4 py-3 mb-4">
                  {matchedInstitution.logo_url && (
                    <img src={matchedInstitution.logo_url} alt={matchedInstitution.name} className="w-7 h-7 rounded object-cover" />
                  )}
                  <CheckCircle size={20} className="text-teal-600" />
                  <span>Matched: {matchedInstitution.name}</span>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-slate-800 bg-white border border-slate-300 hover:border-slate-400 transition-all"
                  onClick={() => setCurrentStep(1)}
                >
                  <ArrowLeft size={18} /> Back
                </button>
                <button
                  type="button"
                  className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-white bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                  onClick={() => setCurrentStep(3)}
                  disabled={!termsAccepted}
                >
                  Next <ArrowRight size={18} />
                </button>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div>
              <div className="flex flex-col items-center mb-6">
                <div
                  className="w-full h-40 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 cursor-pointer hover:border-teal-600 overflow-hidden transition-all"
                  onClick={() => !idVerified && verifyIdInputRef.current?.click()}
                >
                  {verifyIdPreview ? (
                    <img src={verifyIdPreview} alt="ID card preview" className="w-full h-full object-contain" />
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload size={22} />
                      <span className="text-xs">Click to upload your ID card</span>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  ref={verifyIdInputRef}
                  accept="image/*"
                  onChange={handleVerifyIdFileChange}
                  className="hidden"
                  disabled={isVerifyingId || idVerified}
                />
                {verifyIdFile && !idVerified && (
                  <button type="button" className="text-xs text-slate-400 hover:text-red-500 mt-2" onClick={removeVerifyIdFile}>
                    Remove
                  </button>
                )}
              </div>

              {verifyIdError && <p className="text-red-600 text-xs text-center mb-4">{verifyIdError}</p>}

              {idVerified ? (
                <div className="flex items-center justify-center gap-3 bg-teal-50 border border-teal-200 text-teal-700 font-semibold rounded-xl px-4 py-3 mb-4">
                  <CheckCircle size={20} className="text-teal-600" />
                  <span>ID Verified</span>
                </div>
              ) : (
                <div className="flex gap-3">
                  <button
                    type="button"
                    className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-slate-800 bg-white border border-slate-300 hover:border-slate-400 transition-all"
                    onClick={() => setCurrentStep(2)}
                    disabled={isVerifyingId}
                  >
                    <ArrowLeft size={18} /> Back
                  </button>
                  <button
                    type="button"
                    className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-white bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                    onClick={handleVerifyId}
                    disabled={!verifyIdFile || isVerifyingId}
                  >
                    {isVerifyingId ? 'Verifying...' : 'Verify ID'}
                  </button>
                </div>
              )}
            </div>
          )}

          {currentStep === 4 && (
            <div className="text-center py-6">
              <CheckCircle className="mx-auto text-teal-600" size={48} />
              <h3 className="text-lg font-semibold text-slate-800 mt-3">You're all set!</h3>
              <p className="text-slate-500 mt-1">Your student account is ready to go.</p>
              <button
                onClick={handleFinishSignup}
                disabled={isLoading}
                className="mt-6 w-full bg-teal-600 text-white font-semibold py-3 rounded-xl hover:bg-teal-700 disabled:opacity-50"
              >
                {isLoading ? 'Finishing...' : 'Go to Dashboard'}
              </button>
              {errorMessage && <p className="text-red-600 text-sm mt-3">{errorMessage}</p>}
            </div>
          )}
        </div>
      </div>

      {showTermsModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => !isConfirming && setShowTermsModal(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-800">Terms &amp; Conditions</h2>
              <p className="text-sm text-slate-500 mt-1">Scroll to the bottom to enable Confirm</p>
            </div>

            <div
              onScroll={handleTermsScroll}
              className="p-6 overflow-y-auto flex-1 text-sm text-slate-700 space-y-5"
            >
              {TERMS_SECTIONS.map((section) => (
                <div key={section.title}>
                  <h3 className="font-semibold text-slate-800 mb-1">{section.title}</h3>
                  {section.content && <p>{section.content}</p>}
                  {section.subtitle && <p className="font-medium text-slate-700 mt-2">{section.subtitle}</p>}
                  {section.subpoints && (
                    <ul className="list-none space-y-1 mt-1">
                      {section.subpoints.map((point, i) => (
                        <li key={i}>{point}</li>
                      ))}
                    </ul>
                  )}
                  {section.additional && <p className="mt-2">{section.additional}</p>}
                  {section.subcategories &&
                    section.subcategories.map((subcat, i) => (
                      <div key={i} className="mt-3">
                        {subcat.subtitle && <p className="font-medium text-slate-700">{subcat.subtitle}</p>}
                        {subcat.subpoints && (
                          <ul className="list-none space-y-1 mt-1">
                            {subcat.subpoints.map((point, j) => (
                              <li key={j}>{point}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                </div>
              ))}
            </div>

            {confirmError && (
              <div className="px-6 py-2 text-red-600 text-sm border-t border-slate-100">{confirmError}</div>
            )}

            <div className="p-6 border-t border-slate-200 flex gap-3">
              <button
                type="button"
                className="flex-1 py-3 rounded-xl font-semibold text-slate-800 bg-white border border-slate-300 hover:border-slate-400 transition-all disabled:opacity-60"
                onClick={() => setShowTermsModal(false)}
                disabled={isConfirming}
              >
                Cancel
              </button>
              <button
                type="button"
                className="flex-1 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                onClick={handleConfirmTerms}
                disabled={!hasScrolledToBottom || isConfirming}
              >
                {isConfirming ? 'Confirming...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StudentProgramSignup;
